import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router';
import config from '../utils/config';
import Button from '../components/ui/Button';
import AuthPageLayout from '../components/AuthPageLayout';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';

// Plan 28: public set/reset password page reached from invite + reset emails.
// No auth — possession of a valid token is the credential. The token rides in
// the URL FRAGMENT (#token=...), never a path segment: fragments aren't sent
// to servers, so raw tokens stay out of access logs and referer headers.
// Validation + consumption both POST the token in the request body.

const MIN_PASSWORD_LENGTH = 12; // mirrors accounts-api password policy (Plan 68)

const POLICY_REASON_TEXT: Record<string, string> = {
  too_short: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  common: 'That password is too common — pick something less guessable.',
  breached: 'That password has appeared in known data breaches — choose another.',
};

function friendlyPolicyError(serverMessage: string): string {
  const reason = serverMessage.split(':').pop()?.trim() ?? '';
  return POLICY_REASON_TEXT[reason] ?? serverMessage;
}

type TokenState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'expired' }
  | { kind: 'valid'; email: string; purpose: string };

function tokenFromFragment(): string {
  // location.hash is "#token=<raw>" — raw token is base64url, no decoding needed.
  const match = window.location.hash.match(/token=([A-Za-z0-9_-]+)/);
  return match?.[1] ?? '';
}

const ResetPasswordPage: React.FC = () => {
  const [token] = useState<string>(tokenFromFragment);
  const navigate = useNavigate();
  const [tokenState, setTokenState] = useState<TokenState>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Token captured in state — scrub it from the address bar and history
    // entry so it can't be shoulder-surfed or resurrected from history.
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    let cancelled = false;
    if (!token) {
      setTokenState({ kind: 'invalid' });
      return;
    }
    (async () => {
      try {
        const res = await axios.post(
          `${config.accountsApiUrl}/accounts/validate-token`,
          { token }
        );
        if (cancelled) return;
        if (res.data?.valid) {
          setTokenState({ kind: 'valid', email: res.data.email, purpose: res.data.purpose });
        } else if (res.data?.expired) {
          setTokenState({ kind: 'expired' });
        } else {
          setTokenState({ kind: 'invalid' });
        }
      } catch {
        if (!cancelled) setTokenState({ kind: 'invalid' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(POLICY_REASON_TEXT.too_short);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${config.accountsApiUrl}/accounts/reset-password`, {
        token,
        password,
      });
      navigate('/login?reset=success', { replace: true });
    } catch (err) {
      const msg = axios.isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : undefined;
      setError(msg ? friendlyPolicyError(msg) : 'Failed to set password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (tokenState.kind === 'loading') {
    return <AuthPageLayout title="Checking link"><p role="status">Checking link…</p></AuthPageLayout>;
  }

  if (tokenState.kind === 'expired') {
    return (
      <AuthPageLayout title="Link expired">
        <p className="text-foreground">
          This link has expired. Ask an admin to send you a new one, or use{' '}
          <Link to="/forgot-password" className="text-blue-700 underline" title="Request a new password reset link">
            Forgot password
          </Link>{' '}
          if you already have an account.
        </p>
      </AuthPageLayout>
    );
  }

  if (tokenState.kind === 'invalid') {
    return (
      <AuthPageLayout title="Invalid link">
        <p className="text-foreground">This link is invalid or has already been used.</p>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout title={tokenState.purpose === 'invite' ? 'Set up your account' : 'Reset your password'}>
      <p className="mb-4 text-muted-foreground">
        for <span className="font-mono">{tokenState.email}</span>
      </p>
      {error && <p id="reset-error" className="mb-4 text-sm text-red-700" role="alert">{error}</p>}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <Label htmlFor="new-password">New password</Label>
          <p id="new-password-help" className="mb-2 text-sm text-muted-foreground">Use at least {MIN_PASSWORD_LENGTH} characters; avoid common passwords.</p>
          <Input
            id="new-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            aria-describedby={`new-password-help${error ? ' reset-error' : ''}`}
            aria-invalid={error ? true : undefined}
          />
        </div>
        <div className="mb-4">
          <Label htmlFor="confirm-password">Confirm password</Label>
          <p id="confirm-password-help" className="mb-2 text-sm text-muted-foreground">Enter the same password again to confirm it.</p>
          <Input
            id="confirm-password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            aria-describedby={`confirm-password-help${error ? ' reset-error' : ''}`}
            aria-invalid={error ? true : undefined}
          />
        </div>
        <Button
          variant="primary"
          type="submit"
          disabled={submitting}
          className="w-full"
          title="Save this as your new password; you can then log in with it"
        >
          {submitting ? 'Saving…' : 'Set password'}
        </Button>
      </form>
    </AuthPageLayout>
  );
};

export default ResetPasswordPage;
